import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { PageHeader } from '../components/PageHeader';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { 
    Calendar, Clock, MapPin, Users, Coins, ChevronRight,
    Sparkles, Heart, Palette, Music, Compass, Star,
    Check, AlertCircle, X, User, CalendarPlus
} from 'lucide-react';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '../components/ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_CONFIG = {
    move_well: { icon: Heart, label: 'Move Well', color: 'emerald', gradient: 'from-emerald-500 to-teal-600', bg: 'bg-emerald-50', text: 'text-emerald-700' },
    connect_well: { icon: Users, label: 'Connect Well', color: 'blue', gradient: 'from-blue-500 to-indigo-600', bg: 'bg-blue-50', text: 'text-blue-700' },
    create_well: { icon: Palette, label: 'Create Well', color: 'purple', gradient: 'from-purple-500 to-pink-600', bg: 'bg-purple-50', text: 'text-purple-700' },
    feel_well: { icon: Sparkles, label: 'Feel Well', color: 'amber', gradient: 'from-amber-500 to-orange-600', bg: 'bg-amber-50', text: 'text-amber-700' },
    explore_well: { icon: Compass, label: 'Explore Well', color: 'cyan', gradient: 'from-cyan-500 to-blue-600', bg: 'bg-cyan-50', text: 'text-cyan-700' },
    celebrate_well: { icon: Music, label: 'Celebrate Well', color: 'rose', gradient: 'from-rose-500 to-red-600', bg: 'bg-rose-50', text: 'text-rose-700' }
};

function ActivityCard({ activity, onBook, userCredits, isBooked }) {
    const config = CATEGORY_CONFIG[activity.category] || CATEGORY_CONFIG.connect_well;
    const Icon = config.icon;
    const activityDate = new Date(activity.date_time);
    const spotsLeft = activity.capacity - (activity.current_bookings || 0);
    const isFull = spotsLeft <= 0;
    const canAfford = activity.is_free || userCredits >= activity.credit_cost;
    
    const handleAddToCalendar = (e) => {
        e.stopPropagation();
        window.open(`${process.env.REACT_APP_BACKEND_URL}/api/calendar/activity/${activity.id}.ics`, '_blank');
    };
    
    return (
        <div 
            className="bg-white rounded-2xl shadow-sm border border-stone-200 overflow-hidden hover:shadow-md transition-shadow"
            data-testid={`activity-card-${activity.id}`}
        >
            {/* Activity Image */}
            {activity.image_url && (
                <div className="relative h-40 overflow-hidden">
                    <img 
                        src={activity.image_url} 
                        alt={activity.title}
                        className="w-full h-full object-cover"
                        onError={(e) => { e.target.style.display = 'none'; }}
                    />
                    <div className={`absolute inset-0 bg-gradient-to-t from-black/50 to-transparent`} />
                    <div className={`absolute top-3 left-3 ${config.bg} ${config.text} px-3 py-1 rounded-full text-xs font-semibold flex items-center gap-1`}>
                        <Icon className="w-3 h-3" />
                        {config.label}
                    </div>
                    {activity.is_free ? (
                        <span className="absolute top-3 right-3 bg-emerald-100 text-emerald-700 px-3 py-1 rounded-full text-xs font-bold">FREE</span>
                    ) : (
                        <span className="absolute top-3 right-3 bg-white/90 px-3 py-1 rounded-full flex items-center gap-1 text-amber-600 font-bold text-xs">
                            <Coins className="w-3 h-3" />
                            {activity.credit_cost}
                        </span>
                    )}
                </div>
            )}
            
            {!activity.image_url && <div className={`h-2 bg-gradient-to-r ${config.gradient}`} />}
            
            <div className="p-5">
                {!activity.image_url && (
                    <div className="flex items-start justify-between mb-3">
                        <div className={`${config.bg} ${config.text} px-3 py-1 rounded-full text-xs font-semibold flex items-center gap-1`}>
                            <Icon className="w-3 h-3" />
                            {config.label}
                        </div>
                        {activity.is_free ? (
                            <span className="bg-emerald-100 text-emerald-700 px-3 py-1 rounded-full text-xs font-bold">FREE</span>
                        ) : (
                            <span className="flex items-center gap-1 text-amber-600 font-bold">
                                <Coins className="w-4 h-4" />
                                {activity.credit_cost}
                            </span>
                        )}
                    </div>
                )}
                
                <h3 className="font-bold text-stone-900 text-lg mb-2">{activity.title}</h3>
                <p className="text-stone-600 text-sm line-clamp-2 mb-4">{activity.description}</p>
                
                <div className="space-y-2 text-sm text-stone-500 mb-4">
                    <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4" />
                        <span>{activityDate.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' })}</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4" />
                        <span>{activityDate.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <MapPin className="w-4 h-4" />
                        <span>{activity.location}</span>
                    </div>
                    {activity.facilitator && (
                        <div className="flex items-center gap-2">
                            <User className="w-4 h-4" />
                            <span>{activity.facilitator}</span>
                        </div>
                    )}
                </div>
                
                <div className="flex items-center justify-between pt-4 border-t border-stone-100">
                    <div className="flex items-center gap-3">
                        <div className="flex items-center gap-1 text-sm">
                            <Users className="w-4 h-4 text-stone-400" />
                            {isFull ? (
                                <span className="text-amber-600 font-medium">Full</span>
                            ) : (
                                <span className="text-stone-500">{spotsLeft} spots</span>
                            )}
                        </div>
                        <button
                            onClick={handleAddToCalendar}
                            className="text-stone-400 hover:text-emerald-600 transition-colors p-1"
                            title="Add to Calendar"
                        >
                            <CalendarPlus className="w-4 h-4" />
                        </button>
                    </div>
                    
                    {isBooked ? (
                        <span className="flex items-center gap-1 text-emerald-600 font-semibold">
                            <Check className="w-5 h-5" />
                            Booked
                        </span>
                    ) : (
                        <Button
                            onClick={() => onBook(activity)}
                            disabled={!canAfford && !activity.is_free}
                            className={`rounded-full px-5 ${
                                isFull 
                                    ? 'bg-amber-500 hover:bg-amber-600' 
                                    : 'bg-emerald-700 hover:bg-emerald-800'
                            }`}
                            data-testid={`book-btn-${activity.id}`}
                        >
                            {isFull ? 'Join Waitlist' : 'Book Now'}
                        </Button>
                    )}
                </div>
                
                {!canAfford && !activity.is_free && !isBooked && (
                    <p className="text-amber-600 text-xs mt-2 flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" />
                        Insufficient credits
                    </p>
                )}
            </div>
        </div>
    );
}

export function ActivitiesPage() {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const { token, profile } = useAuth();
    const [activities, setActivities] = useState([]);
    const [myBookings, setMyBookings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || 'all');
    const [bookingModal, setBookingModal] = useState({ open: false, activity: null });
    const [booking, setBooking] = useState(false);

    useEffect(() => {
        fetchData();
    }, [token]);

    const fetchData = async () => {
        try {
            const [activitiesRes, bookingsRes] = await Promise.all([
                axios.get(`${API}/activities`),
                token ? axios.get(`${API}/member/bookings`, { headers: { Authorization: `Bearer ${token}` } }) : Promise.resolve({ data: [] })
            ]);
            
            setActivities(activitiesRes.data);
            setMyBookings(bookingsRes.data);
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleCategoryChange = (category) => {
        setSelectedCategory(category);
        if (category === 'all') {
            searchParams.delete('category');
        } else {
            searchParams.set('category', category);
        }
        setSearchParams(searchParams);
    };

    const handleBook = (activity) => {
        if (!token) {
            navigate('/login');
            return;
        }
        setBookingModal({ open: true, activity });
    };

    const confirmBooking = async () => {
        if (!bookingModal.activity) return;
        
        setBooking(true);
        try {
            const response = await axios.post(
                `${API}/bookings`,
                { activity_id: bookingModal.activity.id },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            
            toast.success(response.data.message);
            setBookingModal({ open: false, activity: null });
            fetchData(); // Refresh data
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to book activity');
        } finally {
            setBooking(false);
        }
    };

    const filteredActivities = selectedCategory === 'all' 
        ? activities 
        : activities.filter(a => a.category === selectedCategory);

    const bookedActivityIds = myBookings
        .filter(b => b.status === 'confirmed' || b.status === 'waitlist')
        .map(b => b.activity_id);

    return (
        <div className="min-h-screen bg-stone-50" data-testid="activities-page">
            <PageHeader title="Activities" showBack />
            
            {/* Category Filter */}
            <div className="px-5 py-4 bg-white border-b border-stone-200 sticky top-0 z-10">
                <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
                    <button
                        onClick={() => handleCategoryChange('all')}
                        className={`px-4 py-2 rounded-full whitespace-nowrap text-sm font-medium transition-colors ${
                            selectedCategory === 'all'
                                ? 'bg-emerald-700 text-white'
                                : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
                        }`}
                    >
                        All Activities
                    </button>
                    {Object.entries(CATEGORY_CONFIG).map(([key, config]) => {
                        const Icon = config.icon;
                        return (
                            <button
                                key={key}
                                onClick={() => handleCategoryChange(key)}
                                className={`px-4 py-2 rounded-full whitespace-nowrap text-sm font-medium transition-colors flex items-center gap-1.5 ${
                                    selectedCategory === key
                                        ? `bg-gradient-to-r ${config.gradient} text-white`
                                        : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
                                }`}
                            >
                                <Icon className="w-4 h-4" />
                                {config.label}
                            </button>
                        );
                    })}
                </div>
            </div>
            
            {/* Credits Banner */}
            {profile?.membership_status === 'active' && (
                <div className="px-5 py-3 bg-gradient-to-r from-amber-50 to-yellow-50 border-b border-amber-200">
                    <div className="flex items-center justify-between">
                        <span className="text-sm text-amber-800">
                            Your balance: <strong>{profile.credits_balance} credits</strong>
                        </span>
                        <button
                            onClick={() => navigate('/residents/credits')}
                            className="text-amber-700 text-sm font-medium"
                        >
                            Buy more →
                        </button>
                    </div>
                </div>
            )}
            
            {/* Activities List */}
            <div className="px-5 py-6 pb-24">
                {loading ? (
                    <div className="flex justify-center py-12">
                        <div className="w-10 h-10 border-4 border-emerald-600 border-t-transparent rounded-full animate-spin" />
                    </div>
                ) : filteredActivities.length === 0 ? (
                    <div className="text-center py-12">
                        <Calendar className="w-16 h-16 text-stone-300 mx-auto mb-4" />
                        <h3 className="text-lg font-semibold text-stone-900">No activities found</h3>
                        <p className="text-stone-500 mt-1">Check back soon for new experiences</p>
                    </div>
                ) : (
                    <div className="space-y-4" data-testid="activities-list">
                        {filteredActivities.map((activity) => (
                            <ActivityCard
                                key={activity.id}
                                activity={activity}
                                onBook={handleBook}
                                userCredits={profile?.credits_balance || 0}
                                isBooked={bookedActivityIds.includes(activity.id)}
                            />
                        ))}
                    </div>
                )}
            </div>
            
            {/* Booking Confirmation Modal */}
            <Dialog open={bookingModal.open} onOpenChange={(open) => !open && setBookingModal({ open: false, activity: null })}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle>Confirm Booking</DialogTitle>
                    </DialogHeader>
                    
                    {bookingModal.activity && (
                        <div className="space-y-4">
                            <div className="bg-stone-50 rounded-xl p-4">
                                <h4 className="font-bold text-stone-900">{bookingModal.activity.title}</h4>
                                <div className="mt-2 space-y-1 text-sm text-stone-600">
                                    <p className="flex items-center gap-2">
                                        <Calendar className="w-4 h-4" />
                                        {new Date(bookingModal.activity.date_time).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' })}
                                    </p>
                                    <p className="flex items-center gap-2">
                                        <Clock className="w-4 h-4" />
                                        {new Date(bookingModal.activity.date_time).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                                    </p>
                                    <p className="flex items-center gap-2">
                                        <MapPin className="w-4 h-4" />
                                        {bookingModal.activity.location}
                                    </p>
                                </div>
                            </div>
                            
                            {!bookingModal.activity.is_free && (
                                <div className="bg-amber-50 rounded-xl p-4 flex items-center justify-between">
                                    <span className="text-amber-800">Credits required</span>
                                    <span className="font-bold text-amber-700 flex items-center gap-1">
                                        <Coins className="w-5 h-5" />
                                        {bookingModal.activity.credit_cost}
                                    </span>
                                </div>
                            )}
                            
                            {bookingModal.activity.current_bookings >= bookingModal.activity.capacity && (
                                <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl p-4 text-sm text-white">
                                    <p className="font-medium">This activity is full</p>
                                    <p className="text-blue-100">You'll be added to the waitlist. Credits won't be charged until a spot opens.</p>
                                </div>
                            )}
                            
                            <div className="flex gap-3">
                                <Button
                                    variant="outline"
                                    onClick={() => setBookingModal({ open: false, activity: null })}
                                    className="flex-1"
                                >
                                    Cancel
                                </Button>
                                <Button
                                    onClick={confirmBooking}
                                    disabled={booking}
                                    className="flex-1 bg-emerald-700 hover:bg-emerald-800"
                                    data-testid="confirm-booking-btn"
                                >
                                    {booking ? 'Booking...' : 'Confirm Booking'}
                                </Button>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}

export default ActivitiesPage;
